import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, Send } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { judgmentsSubmitFeedback } from '@/client';

interface FeedbackFormProps {
  judgmentId: string;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({ judgmentId }) => {
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitFeedback = useMutation({
    mutationFn: async (feedback: string) => {
      return judgmentsSubmitFeedback({
        judgmentId,
        requestBody: {
          feedback_text: feedback,
          judgment_id: judgmentId,
        }
      });
    },
    onSuccess: () => {
      toast({
        title: 'Tack för din feedback!',
        description: 'Din feedback har skickats in.',
        variant: 'default',
      });
      setFeedbackText('');
      setIsSubmitting(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Ett fel uppstod',
        description: error.message || 'Kunde inte skicka in feedback. Försök igen senare.',
        variant: 'destructive',
      });
      setIsSubmitting(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!feedbackText.trim()) {
      toast({
        title: 'Feedback krävs',
        description: 'Vänligen skriv in din feedback innan du skickar.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    submitFeedback.mutate(feedbackText);
  };

  return (
    <Card className="p-6 bg-gradient-card shadow-card-legal border border-primary/20">
      <div className="flex items-center space-x-2 mb-4">
        <MessageSquare className="h-5 w-5 text-primary" />
        <h3 className="text-xl font-semibold text-foreground">Ge feedback</h3>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Textarea
            placeholder="Något som inte stämmer?"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            className="min-h-[120px] resize-none"
            disabled={isSubmitting}
          />
          <p className="text-sm text-muted-foreground mt-2">
            Din feedback hjälper oss att förbättra tjänsten.
          </p>
        </div>
        
        <Button
          type="submit"
          disabled={isSubmitting || !feedbackText.trim()}
          className="w-full sm:w-auto rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-card-legal"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
              Skickar...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Skicka feedback
            </>
          )}
        </Button>
      </form>
    </Card>
  );
};

export default FeedbackForm;
